import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, Download } from "lucide-react";

export default function BaselineAdmin() {
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState(null);
  const [priority, setPriority] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadRes = () => api.get("/admin/baseline/reservations").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/baseline/settings").then((r) => setSettings(r.data)).catch(() => {});
    loadRes();
    api.get("/admin/baseline/priority").then((r) => setPriority(r.data)).catch(() => {});
    api.get("/admin/baseline/funnel").then((r) => setFunnel(r.data)).catch(() => {});
  }, []);

  const uploadFindings = async (id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/admin/baseline/reservations/${id}/findings`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Findings uploaded and client notified");
      loadRes();
    } catch { toast.error("Upload failed"); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/baseline/settings", settings); setSettings(r.data); toast.success("Settings saved"); }
    catch { toast.error("Could not save settings"); } finally { setSaving(false); }
  };

  const updateRes = async (id, patch) => {
    try { await api.put(`/admin/baseline/reservations/${id}`, patch); loadRes(); }
    catch { toast.error("Update failed"); }
  };

  const exportCsv = () => {
    const rows = data?.reservations || [];
    const cols = ["firm_name", "contact_name", "email", "phone", "status", "paid", "production_window", "created_at"];
    const csv = [cols.join(",")].concat(rows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "baseline-reservations.csv"; a.click(); URL.revokeObjectURL(url);
  };

  if (!settings || !data) return <div className="font-mono-data text-sm text-zinc-500">Loading…</div>;

  return (
    <div className="pb-8" data-testid="baseline-admin-page">
      <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Campaign</p>
      <h1 className="font-display text-4xl font-light mt-1">Baseline Review — Admin</h1>

      {/* Settings */}
      <div className="mt-6 border border-zinc-200 bg-white p-6" data-testid="baseline-settings-panel">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl">Campaign settings</h2>
          <Button onClick={saveSettings} disabled={saving} className="rounded-none bg-[#0B1F33] text-white" data-testid="save-baseline-settings">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Row><span>Homepage promotional banner</span><Switch checked={settings.banner_enabled} onCheckedChange={(v) => setSettings({ ...settings, banner_enabled: v })} data-testid="toggle-banner" /></Row>
          <Row><span>Reservations open</span><Switch checked={settings.reservations_open} onCheckedChange={(v) => setSettings({ ...settings, reservations_open: v })} data-testid="toggle-reservations" /></Row>
          <div><Label className="text-[11px] uppercase tracking-widest font-mono-data text-zinc-500">Introductory price ($)</Label><Input type="number" value={settings.intro_price} onChange={(e) => setSettings({ ...settings, intro_price: Number(e.target.value) })} className="rounded-none mt-1" data-testid="input-intro-price" /></div>
          <div><Label className="text-[11px] uppercase tracking-widest font-mono-data text-zinc-500">Regular value ($)</Label><Input type="number" value={settings.regular_value} onChange={(e) => setSettings({ ...settings, regular_value: Number(e.target.value) })} className="rounded-none mt-1" /></div>
          <div className="sm:col-span-2"><Label className="text-[11px] uppercase tracking-widest font-mono-data text-zinc-500">Availability language</Label><Input value={settings.availability_text} onChange={(e) => setSettings({ ...settings, availability_text: e.target.value })} className="rounded-none mt-1" /></div>
          <div className="sm:col-span-2"><Label className="text-[11px] uppercase tracking-widest font-mono-data text-zinc-500">Paused / priority-list message</Label><Textarea rows={2} value={settings.paused_message} onChange={(e) => setSettings({ ...settings, paused_message: e.target.value })} className="rounded-none mt-1" /></div>
        </div>
      </div>

      {/* Conversion funnel */}
      {funnel && (
        <div className="mt-8 border border-zinc-200 bg-white p-6" data-testid="baseline-funnel-panel">
          <h2 className="font-display text-xl mb-1">Conversion funnel</h2>
          <p className="text-xs text-zinc-500 mb-5">Unique sessions per stage (from tracked campaign events).</p>
          <div className="space-y-3">
            {funnel.steps.map((s) => (
              <div key={s.event} data-testid={`funnel-${s.event}`}>
                <div className="flex justify-between text-xs text-zinc-600 mb-1">
                  <span>{s.label}</span>
                  <span className="font-mono-data">{s.count} · {s.pct}%</span>
                </div>
                <div className="h-2 bg-zinc-100">
                  <div className="h-2 bg-[#174A7E]" style={{ width: `${Math.max(s.pct, 1)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reservations */}
      <div className="mt-8 flex items-center justify-between">
        <h2 className="font-display text-2xl font-light">Reservations ({data.reservations.length})</h2>
        <Button onClick={exportCsv} variant="outline" className="rounded-none border-zinc-300" data-testid="export-csv"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
      </div>
      <div className="mt-4 border border-zinc-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Firm</TableHead><TableHead>Contact</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead><TableHead>Production window</TableHead><TableHead>Docs</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.reservations.map((r) => (
              <TableRow key={r.id} data-testid={`reservation-${r.id}`}>
                <TableCell className="text-sm">
                  <div className="font-medium text-zinc-800">{r.firm_name}</div>
                  <div className="text-xs text-zinc-500">{r.email}</div>
                  {r.flags?.length > 0 && <div className="text-[10px] text-[#B54747] mt-0.5">⚑ {r.flags.join(", ")}</div>}
                </TableCell>
                <TableCell className="text-sm text-zinc-600">{r.contact_name}<div className="text-xs text-zinc-400">{r.phone}</div></TableCell>
                <TableCell>{r.paid ? <span className="text-[#156F52] text-xs font-medium">Paid</span> : <span className="text-zinc-400 text-xs">Pending</span>}</TableCell>
                <TableCell>
                  <Select value={r.status} onValueChange={(v) => updateRes(r.id, { status: v })}>
                    <SelectTrigger className="rounded-none h-9 w-52 text-xs" data-testid={`status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{data.statuses.map((s) => <SelectItem key={s} value={s} className="text-xs">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Input defaultValue={r.production_window} onBlur={(e) => e.target.value !== r.production_window && updateRes(r.id, { production_window: e.target.value })} placeholder="e.g. Wk of Jul 14" className="rounded-none h-9 w-40 text-xs" /></TableCell>
                <TableCell className="text-sm">
                  {(r.documents || []).map((d, i) => (
                    <a key={i} href={`${api.defaults.baseURL}/admin/baseline/document/${r.id}/${i}`} className="block text-xs text-[#0E7C7B] hover:underline" target="_blank" rel="noreferrer">{d.name}</a>
                  ))}
                  {(!r.documents || r.documents.length === 0) && <span className="text-xs text-zinc-400">—</span>}
                  <div className="mt-2 pt-2 border-t border-zinc-100">
                    {r.findings ? (
                      <span className="text-[10px] text-[#156F52] block">Findings: {r.findings.name}</span>
                    ) : (
                      <span className="text-[10px] text-zinc-400 block">No findings yet</span>
                    )}
                    <label className="text-[10px] text-[#174A7E] hover:underline cursor-pointer inline-block mt-0.5" data-testid={`upload-findings-${r.id}`}>
                      {r.findings ? "Replace findings" : "Upload findings"}
                      <input type="file" className="hidden" onChange={(e) => { uploadFindings(r.id, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {data.reservations.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-zinc-400 py-8">No reservations yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Notes per reservation */}
      {data.reservations.some((r) => r.status) && (
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {data.reservations.map((r) => (
            <div key={r.id} className="border border-zinc-200 bg-white p-4">
              <p className="text-sm font-medium text-zinc-800">{r.firm_name}</p>
              <Textarea rows={2} defaultValue={r.admin_notes} onBlur={(e) => e.target.value !== (r.admin_notes || "") && updateRes(r.id, { admin_notes: e.target.value })} placeholder="Internal notes…" className="rounded-none mt-2 text-sm" />
            </div>
          ))}
        </div>
      )}

      {/* Priority list */}
      <h2 className="font-display text-2xl font-light mt-10">Priority list ({priority.length})</h2>
      <div className="mt-4 border border-zinc-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Firm</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Monthly spend</TableHead></TableRow></TableHeader>
          <TableBody>
            {priority.map((p) => (<TableRow key={p.id}><TableCell className="text-sm">{p.name}</TableCell><TableCell className="text-sm">{p.firm_name}</TableCell><TableCell className="text-sm">{p.email}</TableCell><TableCell className="text-sm">{p.phone}</TableCell><TableCell className="text-sm">{p.monthly_spend}</TableCell></TableRow>))}
            {priority.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-400 py-8">No priority-list signups yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const Row = ({ children }) => (<div className="flex items-center justify-between border border-zinc-200 px-4 h-11 text-sm text-zinc-700">{children}</div>);
